import React, { FC, ReactNode, SyntheticEvent, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import groupBy from 'lodash.groupby';

import { getParams } from '@helpers';
import { useQueryParams } from '@hooks';
import {
  CollapseTable,
  LinkStatusFilter,
  SearchOptionsCheckbox,
  SubmitInfringingLinkButton,
} from '@molecules';
import { SUBMIT_INFRINGING_QUERY_NAME } from '@molecules/SubmitInfringingLinkButton/SubmitInfringingLinkButton';
import { renderDateFilter } from '@molecules/Table/filters';
import { Box, Checkbox, SelectChangeEvent } from '@mui/material';
import {
  FilterItem,
  LinkAddedType,
  LinkManagementType,
  LinkSourceType,
  LinkStatuses,
  SortingItem,
} from '@services';

import { getColumns } from './utils';
import { LinksTableTabs } from './LinksTableTabs';

const FILTERS_LIST: FilterItem[] = [
  {
    name: 'Status',
    key: 'status',
    getFilter: (onChange, onClose, setFilters, filter): ReactNode => (
      <LinkStatusFilter
        key="status"
        name="status"
        value={filter.status as LinkStatuses}
        onChange={(event: SelectChangeEvent<unknown>) =>
          setFilters({ ...filter, status: event.target.value as string })
        }
        onClose={onClose}
      />
    ),
  },
  {
    name: 'Period',
    key: 'lastModified',
    resetFields: ['updatedStartDate', 'updatedEndDate'],
    getFilter: renderDateFilter({
      label: 'Period',
      key: 'lastModified',
      resetFields: ['updatedStartDate', 'updatedEndDate'],
    }),
  },
  {
    name: 'Search filter',
    key: 'searchFilter',
    getFilter: (onChange, onClose, setFilters, filter): ReactNode => (
      <SearchOptionsCheckbox
        key="isOnlyOneSpaceSearch"
        name="isOnlyOneSpaceSearch"
        control={<Checkbox inputProps={{ 'aria-label': 'controlled' }} />}
        label="Search with one space"
        value={filter.isOnlyOneSpaceSearch}
        onChange={(event: SyntheticEvent<unknown>, checked: boolean) => {
          setFilters({ ...filter, isOnlyOneSpaceSearch: String(checked) });
        }}
        onClose={() => {
          onClose();
          setFilters({ ...filter, isOnlyOneSpaceSearch: String(false) });
        }}
      />
    ),
  },
];

const SORTING_LIST: SortingItem[] = [
  {
    name: 'Update date (Ascending)',
    sortBy: 'updatedAt',
    sortDirection: 'asc',
  },
  {
    name: 'Update date (Descending)',
    sortBy: 'updatedAt',
    sortDirection: 'desc',
  },
  {
    name: 'Date of addition (Ascending)',
    sortBy: 'createdAt',
    sortDirection: 'asc',
  },
  {
    name: 'Date of addition (Descending)',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  },
];

export const LinksTable: FC<{ refetch?: () => void }> = (props) => {
  const { assetId, id } = useParams();
  const query = useQueryParams();
  const [searchParams] = useSearchParams();
  const params = getParams(searchParams);
  const [activeTab, setActiveTab] = useState(
    (params as { sourceType?: LinkSourceType })?.sourceType || LinkSourceType.All,
  );
  const [addedType, setAddedType] = useState(
    (params as { addedType?: LinkAddedType })?.addedType || LinkAddedType.All,
  );

  const getSelectedMultipleValue = (items: any[], selectedItemsId: string[]) => {
    const allChildren: LinkManagementType[] = items.reduce(
      (acc, item) => acc.concat(item.child),
      [],
    );
    const selectedItems = allChildren.filter((item) =>
      selectedItemsId.includes((item as { id: string })?.id),
    );
    const uniqStatuses = selectedItems.reduce(
      (statuses: string[], { status }) =>
        statuses.includes(status) ? statuses : [...statuses, status],
      [],
    );
    const status = uniqStatuses.length > 1 ? 'mixed' : uniqStatuses[0];
    const uniqSourceType = selectedItems.reduce(
      (sourceTypes: (string | undefined)[], { sourceType }) =>
        sourceTypes.includes(sourceType) ? sourceTypes : [...sourceTypes, sourceType],
      [],
    );
    const sourceType = uniqSourceType.length > 1 ? 'mixed' : uniqSourceType[0];
    return { status, sourceType };
  };

  const filterText = query.get('filterText') || '';
  const columns = useMemo(() => getColumns(filterText), [filterText]);

  const transformData = (items?: any) => {
    if (!items) {
      return [];
    }

    let result: any = groupBy(items, (item: any) => item.domain);
    result = Object.entries(result).map(([domain, child], index: number) => ({
      label: domain,
      child,
      id: index,
    }));
    return result;
  };

  const onChangeTab = (_e: React.SyntheticEvent, newTab: LinkSourceType) => {
    setActiveTab(newTab);
  };

  const onChangeAddedType = (_e: React.SyntheticEvent, newTab: LinkAddedType) => {
    setAddedType(newTab);
  };

  return (
    <Box>
      <LinksTableTabs
        activeTab={activeTab}
        onChangeTab={onChangeTab}
        addedType={addedType}
        onChangeAddedType={onChangeAddedType}
      />
      <CollapseTable<any>
        searchPlaceholder="Search for Link address"
        queryName={`asset-links-${assetId}`}
        apiRoute={`/admin/models/${assetId}/links`}
        filtersList={FILTERS_LIST}
        sortingList={SORTING_LIST}
        columns={columns}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        defaultQueries={{
          sortBy: 'updatedAt',
          sortDirection: 'desc',
        }}
        preSelectedFilters={{
          sourceType: activeTab === LinkSourceType.All ? undefined : activeTab,
          addedType: addedType === LinkAddedType.All ? undefined : addedType,
        }}
        getSelectedMultipleValue={getSelectedMultipleValue}
        checkboxSelection
        transformData={transformData}
        renderAdditionalControl={
          <SubmitInfringingLinkButton
            assetId={assetId}
            id={id}
            refetchQueryName={`asset-links-${assetId}`}
          />
        }
        refetchQueryName={SUBMIT_INFRINGING_QUERY_NAME(assetId)}
        {...props}
      />
    </Box>
  );
};
